<template>
  <div class="shipment-analysis-container">
    <!-- Top Stats Card -->
    <div class="analysis-card top-card" v-loading="sendCountLoading">
      <div class="stat-item">
        <div class="title">{{ $lang('今日发货量') }}</div>
        <div class="value-row">
          <span class="number"><count-to :start-val="0" :end-val="sendCount.tsendNum || 0" :duration="2000" /></span>
          <span class="radio">{{ $lang('昨日同比') }}</span>
          <span class="trend up" :class="sendCount.lsendRate >= 100 ? 'up' : 'down'">{{ sendCount.lsendRate || 0 }}%</span>
        </div>
      </div>
      <div class="stat-item">
        <div class="title">{{ $lang('本月发货量') }}</div>
        <div class="value-row">
          <span class="number"><count-to :start-val="0" :end-val="sendCount.tmonthSendNum || 0" :duration="2000" /></span>
          <span class="radio">{{ $lang('上月同比') }}</span>
          <span class="trend up" :class="sendCount.lmonthSendRate >= 100 ? 'up' : 'down'">{{ sendCount.lmonthSendRate || 0 }}%</span>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <el-row :gutter="20" class="analysis-card main-content">
      <div class="time-range-selector">
        <el-radio-group v-model="dateType" size="mini" @change="dateTypeChange">
          <el-radio-button label="1">{{ $lang('近7日') }}</el-radio-button>
          <el-radio-button label="2">{{ $lang('近1月') }}</el-radio-button>
          <el-radio-button label="3">{{ $lang('近3月') }}</el-radio-button>
        </el-radio-group>
      </div>
      <el-col :span="24" class="bottom-section">
        <div v-loading="takeCountLoading">
          <div class="collection-card collection-volume">
            <div class="label">{{ $lang('及时揽收量') }}</div>
            <div class="val">
              <count-to :start-val="0" :end-val="takeCount.timeTakeNum || 0" :duration="2000" separator="," />
            </div>
          </div>
          <div class="collection-card collection-rate">
            <div class="info">
              <div class="label">{{ $lang('及时揽收率') }}</div>
              <div class="val">{{ takeCount.timeTakeRate }}%</div>
            </div>
            <div class="chart" ref="rateChart"></div>
          </div>
        </div>
        <div class="trend-card" v-loading="sendTrendLoading">
          <div class="chart-body" ref="trendChart"></div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import CountTo from 'vue-count-to'
import * as echarts from 'echarts'
import { reqSendCount, reqTakeCount, reqSendTrend } from '@api/module/dashboard'
import dayjs from 'dayjs'

export default {
  name: 'ShipmentAnalysis',
  components: { CountTo },
  data() {
    return {
      sendCountLoading: false,
      takeCountLoading: false,
      sendTrendLoading: false,
      sendCount: {},
      takeCount: {},
      sendTrend: [],
      trendChart: null,
      rateChart: null,
      dateType: '1',
    }
  },
  mounted() {
    this.getSendCount()
    this.getTakeCount()
    this.getSendTrend()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.trendChart) this.trendChart.dispose()
    if (this.rateChart) this.rateChart.dispose()
  },
  methods: {
    handleResize() {
      if (this.trendChart) this.trendChart.resize()
      if (this.rateChart) this.rateChart.resize()
    },
    async getSendCount() {
      try {
        this.sendCountLoading = true
        const res = await reqSendCount({})
        if (res.code === 1) {
          this.sendCount = res.data
        }
      } finally {
        this.sendCountLoading = false
      }
    },
    async getTakeCount() {
      const params = {
        dateType: this.dateType,
      }
      try {
        this.takeCountLoading = true
        const res = await reqTakeCount(params)
        if (res.code === 1) {
          this.takeCount = res.data
        }
        this.initRateChart()
      } finally {
        this.takeCountLoading = false
      }
    },
    async getSendTrend() {
      const params = {
        dateType: this.dateType,
      }
      try {
        this.sendTrendLoading = true
        const res = await reqSendTrend(params)
        if (res.code === 1) {
          this.sendTrend = res.data
        }
        this.initTrendChart()
      } finally {
        this.sendTrendLoading = false
      }
    },
    dateTypeChange() {
      this.getTakeCount()
      this.getSendTrend()
    },
    initRateChart() {
      const dom = this.$refs.rateChart
      this.rateChart = echarts.getInstanceByDom(dom) || echarts.init(dom)
      const option = {
        series: [
          {
            type: 'pie',
            radius: ['70%', '85%'],
            avoidLabelOverlap: false,
            label: { show: false },
            emphasis: {
              scale: false,
              label: {
                show: false,
              },
            },
            labelLine: { show: false },
            data: [
              {
                value: this.takeCount.timeTakeRate,
                itemStyle: {
                  color: '#ff4757',
                },
              },
              {
                value: 100 - this.takeCount.timeTakeRate,
                itemStyle: {
                  color: '#f1f2f6',
                },
              },
            ],
          },
        ],
      }
      this.rateChart.clear()
      this.rateChart.setOption(option)
    },
    initTrendChart() {
      const dom = this.$refs.trendChart
      this.trendChart = echarts.getInstanceByDom(dom) || echarts.init(dom)
      const option = {
        title: {
          text: this.$lang('发货量走势'),
          textStyle: {
            color: '#222222',
            fontSize: 16,
            fontWeight: 'bold',
          },
          top: '2%',
          left: '4%',
        },
        tooltip: {
          trigger: 'axis',
        },
        grid: {
          top: '20%',
          left: '3%',
          right: '4%',
          bottom: this.dateType === '1' ? '3%' : '40',
          containLabel: true,
        },
        dataZoom: [
          {
            show: this.dateType !== '1',
            xAxisIndex: 0,
            type: 'slider',
            start: 0,
            end: 100,
            bottom: 20,
            height: 16,
            // 匹配截图的「淡粉色半透明背景」
            backgroundColor: 'rgba(255, 230, 230, 0.2)', // 滑动条整体背景（浅粉透明）
            filterMode: 'filter',
            // 选中区域样式（稍深的淡粉色透明）
            dataBackground: {
              areaStyle: {
                color: 'red',
              },
              lineStyle: {
                color: 'red',
                width: 1,
              },
            },
            // 手柄样式（红色系，匹配折线的红色）
            handleStyle: {
              color: '#ff6666', // 手柄颜色（截图折线的红色系）
              borderColor: '#ff4444', // 手柄边框色
              borderWidth: 2,
              size: 12,
            },
            // 滑动条边框（浅红透明）
            borderColor: 'rgba(255, 200, 200, 0.7)',
            // 文本样式（深粉/红色系）
            textStyle: {
              color: '#e64a4a',
            },
            // 鼠标悬浮时的高亮样式
            emphasis: {
              handleStyle: {
                color: '#ff4444',
                shadowBlur: 5,
              },
            },
            showDataShadow: false,
          },
        ],
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: this.sendTrend.map((item) => (item.date ? dayjs(item.date).format('MM-DD') : '--')),
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#909399' },
          axisPointer: {
            show: true, // 强制开启（5.0部分场景默认隐藏）
            type: 'shadow',
            z: 1, // 5.0新增：提升阴影层级，避免被折线/坐标轴遮挡
            shadowStyle: {
              color: new echarts.graphic.LinearGradient(
                0,
                1, // 渐变起点：y=1（贴近横坐标，底部）
                0,
                0, // 渐变终点：y=0（远离横坐标，顶部）
                [
                  { offset: 1, color: 'rgba(255, 190, 190, 0.1)' }, // 底部（横坐标）：深橙红，高透明度
                  { offset: 0, color: 'rgba(255, 190, 190, 0.6)' }, // 顶部：浅橙红，低透明度
                ]
              ),
              width: 'auto', // 自动匹配x轴类目宽度
              borderRadius: 2, // 可选：阴影圆角，视觉更柔和
            },
          },
        },
        yAxis: {
          type: 'value',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: {
            lineStyle: {
              type: 'dashed',
            },
          },
        },
        series: [
          {
            name: this.$lang('发货量'),
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 8,
            itemStyle: {
              color: '#E60000',
              borderColor: '#fff',
              borderWidth: 2,
            },
            lineStyle: {
              color: '#E60000',
              width: 1,
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(255, 71, 87, 0.2)' },
                { offset: 1, color: 'rgba(255, 71, 87, 0)' },
              ]),
            },
            data: this.sendTrend.map((item) => item.deliveryCount || 0),
          },
        ],
      }
      this.trendChart.clear()
      this.trendChart.setOption(option)
    },
  },
}
</script>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
}
.shipment-analysis-container {
  height: 100%;
  display: flex;
  flex-direction: column;

  .analysis-card {
    background: #fff;
    border-radius: 8px;
    padding: 16px 24px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  }

  .top-card {
    height: 100px;
    margin-bottom: 10px;
    display: flex;
    gap: 32px;
    .stat-item {
      .title {
        font-size: 16px;
        color: #222222;
        font-weight: bold;
        margin-bottom: 12px;
      }
      .value-row {
        display: flex;
        align-items: baseline;
        .number {
          font-size: 32px;
          font-weight: bold;
          color: #1d2129;
          margin-right: 12px;
        }
        .radio {
          font-size: 10px;
          color: #999999;
          margin-right: 8px;
        }
        .trend {
          font-size: 10px;
          display: flex;
          align-items: center;
          &.up {
            color: #2ed573;
          }
          &.down {
            color: #ff4757;
          }
          i {
            margin-left: 2px;
          }
        }
      }
    }
  }
  .time-range-selector {
    display: flex;
    justify-content: flex-end;
  }

  .main-content {
    margin-left: 0 !important;
    margin-right: 0 !important;
    height: 308px;
  }
  .bottom-section {
    flex: 1;
    display: flex;
    gap: 24px;
    padding-top: 16px;
    padding-left: 0 !important;
    padding-right: 0 !important;

    .left-col {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }

    .collection-card {
      width: 260px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      background: #f9fafc;
      box-sizing: border-box;
      padding-left: 20px;
      padding-right: 20px;
      height: 106px;

      &.collection-volume {
        &:nth-child(1) {
          margin-bottom: 20px;
        }
        .label {
          font-size: 16px;
          color: #303133;
          font-weight: bold;
          margin-bottom: 12px;
        }
        .val {
          font-size: 30px;
          font-weight: bold;
          color: #303133;
        }
      }

      &.collection-rate {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;

        .info {
          .label {
            font-size: 16px;
            color: #303133;
            font-weight: bold;
            margin-bottom: 12px;
          }
          .val {
            font-size: 30px;
            font-weight: bold;
            color: #303133;
          }
        }
        .chart {
          width: 80px;
          height: 80px;
        }
      }
    }

    .right-col {
      height: 100%;
    }

    .trend-card {
      height: 230px;
      display: flex;
      flex: 1;
      flex-direction: column;
      background: #f9fafc;
      box-sizing: border-box;

      .chart-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        .title {
          font-size: 16px;
          font-weight: bold;
          color: #303133;
        }
        .filters {
          .filter-tag {
            font-size: 13px;
            padding: 4px 12px;
            cursor: pointer;
            color: #909399;
            margin-left: 8px;
            &.active {
              color: #ff4757;
              background: #ffe2e5;
              border-radius: 4px;
            }
          }
        }
      }

      .chart-body {
        flex: 1;
        min-height: 230px;
      }
    }
  }
}
</style>
